import { create } from "zustand";
import { persist } from "zustand/middleware";

import { api } from "@/api/client";
import type { AuthTenant, AuthUser, RefreshRequest } from "@/api/types";

interface AuthState {
  accessToken: string | null;
  refreshToken: string | null;
  deviceId: string | null;
  expiresAt: number | null;
  user: AuthUser | null;
  tenant: AuthTenant | null;
  isAuthenticated: boolean;
  setSession: (payload: {
    accessToken: string;
    refreshToken: string;
    expiresIn: number;
    deviceId: string;
    user: AuthUser;
    tenant: AuthTenant;
  }) => void;
  setAccessToken: (token: string | null) => void;
  clearSession: () => void;
  getRefreshContext: () => RefreshRequest | null;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      accessToken: null,
      refreshToken: null,
      deviceId: null,
      expiresAt: null,
      user: null,
      tenant: null,
      isAuthenticated: false,
      setSession: ({ accessToken, refreshToken, expiresIn, deviceId, user, tenant }) => {
        set({
          accessToken,
          refreshToken,
          deviceId,
          user,
          tenant,
          expiresAt: Date.now() + expiresIn * 1000,
          isAuthenticated: true,
        });
      },
      setAccessToken: (token) => {
        set((state) => ({
          accessToken: token,
          isAuthenticated: token !== null && state.user !== null,
        }));
      },
      clearSession: () => {
        set({
          accessToken: null,
          refreshToken: null,
          deviceId: null,
          expiresAt: null,
          user: null,
          tenant: null,
          isAuthenticated: false,
        });
      },
      getRefreshContext: () => {
        const state = get();
        if (!state.refreshToken || !state.tenant) {
          return null;
        }
        return {
          refresh_token: state.refreshToken,
          tenant_id: state.tenant.id,
        };
      },
    }),
    {
      name: "seadrive-auth",
      partialize: (state) => ({
        accessToken: state.accessToken,
        refreshToken: state.refreshToken,
        deviceId: state.deviceId,
        expiresAt: state.expiresAt,
        user: state.user,
        tenant: state.tenant,
        isAuthenticated: state.isAuthenticated,
      }),
    },
  ),
);

api.bind({
  getAccessToken: () => useAuthStore.getState().accessToken,
  getRefreshContext: () => useAuthStore.getState().getRefreshContext(),
  setAccessToken: (token) => useAuthStore.getState().setAccessToken(token),
  clearSession: () => useAuthStore.getState().clearSession(),
});
